import { HashLink } from 'react-router-hash-link';

function AegisPlatform() {
  return (
    <div className="bg-projects2 absolute top-0 left-0 min-h-screen w-full bg-gray-100 flex flex-col items-center justify-start px-4 sm:px-8 scroll-mt-[80px] -mt-[80px]">
      
      <div className="w-full max-w-4xl mx-auto pt-2">
        <HashLink
            smooth
            to="/#resume-prof-projects"
            className="inline-flex items-center text-sm font-semibold text-gray-800 border border-blue-200 px-3 py-2 rounded-lg hover:bg-blue-50 hover:border-blue-400 hover:shadow-sm transition-all"
        >
            <span className="mr-1">←</span> Go back to Home
        </HashLink>
      </div>

      <div className="max-w-4xl w-full bg-white rounded-xl shadow-lg p-6 sm:p-10 space-y-6">

        <h1 className="text-4xl font-bold title-color mb-2">
          Agentic Data Intelligence Platform (ADIP)
        </h1>

        <p className="text-md text-gray-500 italic">
          Client: Top-tier global bank | Duration: Nov 2025 – Aug 2026 | Organizational AI Platform Initiative
        </p>

        <p className="text-base text-gray-700 mb-6">
          Architected and developed an LLM-driven, multi-agent orchestration platform that transforms
          ambiguous business queries into structured, executable data workflows. The system bridges
          business intent with controlled data execution by combining orchestration intelligence,
          contextual retrieval, and a guarded execution fabric, enabling real-time generation of
          analytical insights, production-grade SQL, and enterprise ERD artifacts within a unified,
          streaming-first interface.
        </p>

        <ul className="list-disc list-inside space-y-3 text-base text-gray-700 mb-8">

          <li>
            Designed a fully <strong>LLM-driven orchestration engine</strong> using <strong>LangGraph</strong>,
            responsible for intent normalization (ambiguity and sarcasm removal), task decomposition,
            agent routing, and state management. The orchestrator dynamically converts high-level
            user queries into granular, actionable steps, enabling coordinated execution across
            specialized agents while supporting <strong>human-in-the-loop decision checkpoints</strong>.
          </li>

          <li>
            Built a <strong>stateless MCP-backed execution layer</strong> with injected <strong>PySpark sessions</strong>,
            providing controlled access to enterprise data through capabilities such as data discovery,
            multi-table loading, SQL execution, ETL transformations, and metadata/catalog refresh.
            Implemented <strong>strict guardrails</strong> including schema-aware validation, query constraint enforcement,
            and restricted operations to ensure safe and deterministic execution in sandboxed environments.
          </li>

          <li>
            Developed a <strong>Data Query & Insight Generation Agent</strong> that synthesizes user intent,
            orchestrator-refined queries, and context injected from a specialized retrieval system to
            generate optimized SQL for PySpark. The agent executes queries in isolation, analyzes outputs,
            and produces <strong>evidence-backed insights in structured markdown</strong>, along with adaptive
            chat summarization based on token thresholds.
          </li>

          <li>
            Integrated an <strong>ERD Generation Agent</strong> leveraging a custom DSL-based compiler system
            (<a
              href="/erd-script-compiler"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              ERD Script Compiler
            </a>),
            enabling automated transformation of business requirements into fully rendered ERDs. The agent
            collaborates with the orchestration and retrieval layers to perform feasibility analysis, schema
            exploration, and diagram generation, reducing traditional ERD creation timelines from
            <strong> 1–2 days to ~2 minutes</strong>.
          </li>
          
          <li>
            Designed and implemented an <strong>agentic Retrieval-Augmented Generation (RAG) pipeline </strong>
            featuring query classification, intent-aware decomposition, semantic query expansion, retrieval, 
            eranking, and context synthesis to provide domain-specific business knowledge and improve LLM reasoning
            without hardcoded logic.
          </li>

          <li>
            Implemented a <strong>PostgreSQL-based asynchronous checkpointing mechanism</strong> to persist
            conversational state, execution progress, and agent interactions across threads, ensuring
            continuity, recoverability, and scalable session management.
          </li>

          <li>
            Engineered a <strong>streaming-first architecture</strong> using <strong>FastAPI StreamingResponse</strong>,
            enabling real-time propagation of agent outputs to the frontend, eliminating latency associated
            with traditional request-response cycles and improving user interaction responsiveness.
          </li>

          <li>
            Developed a <strong>React-based dual-panel interface</strong> separating final responses from
            execution internals. The main panel presents concise, business-ready outputs, while a collapsible
            <strong>execution trace panel</strong> exposes structured intermediate states, tool invocations,
            and decision justifications—providing transparency into system behavior without exposing raw
            chain-of-thought reasoning.
          </li>

          <li>
            Enabled <strong>dynamic visualization generation</strong> through orchestrator-driven creation of
            <strong>Vega-Lite specifications</strong>, which are automatically rendered and scaled within the UI,
            allowing agents to convert analytical outputs into interactive visual representations without
            manual intervention.
          </li>

          <li>
            Introduced <strong>intelligent caching strategies</strong> at the orchestration layer to minimize
            redundant agent invocations, optimize response latency, and improve system efficiency during
            iterative analytical workflows.
          </li>

        </ul>

        <p className="text-sm text-gray-600">
          <strong>Status:</strong> Core orchestration, agent ecosystem, and execution fabric complete with
          strong internal validation and stakeholder traction. The platform has secured organizational-level
          interest and budget for continued development, with cloud deployment and expanded capabilities
          (towards autonomous AI-driven data engineering workflows) planned in upcoming phases. Early
          evaluations demonstrate <strong>orders-of-magnitude reductions in analytical turnaround time</strong>,
          with multi-day tasks consistently executed within minutes.
        </p>

      </div>
    </div>
  );
}

export default AegisPlatform;